import classNames from "classnames"
import { useState } from "react"
import type { ReactNode } from "react"
import TwoColumnLayout from "../../primitives/TwoColumnLayout"
import MediaGalleryWithThumbnails from "../../molecules/MediaGalleryWithThumbnails"
import type { GalleryItem } from "../../molecules/MediaGalleryWithThumbnails"
import ProductInfoPanel from "../ProductInfoPanel"
import AccordionGroup from "../AccordionGroup"
import AccordionItem from "../../molecules/AccordionItem"
import Card from "../../atoms/Card"
import type { WithActivityTypeFormBulletFragment } from "~/graphql"

const shimmer = "animate-pulse bg-gray-200 rounded"

/**
 * Skeleton placeholder for the media gallery and its thumbnail row. Mirrors the
 * container and dimensions of MediaGalleryWithThumbnails (aspect-[3/2] stage,
 * circular thumbnails) so swapping in real media causes no layout shift.
 */
export const ProductHeroMediaSkeleton = () => (
  <div className="flex flex-col gap-4">
    <div className="w-full aspect-[3/2] rounded-xl bg-blue-100 animate-pulse" />
    <div className="flex flex-wrap justify-center gap-2">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="w-16 h-16 rounded-full bg-blue-100 animate-pulse"
        />
      ))}
    </div>
  </div>
)

ProductHeroMediaSkeleton.displayName = "ProductHeroMediaSkeleton"

/**
 * Skeleton placeholder for the product info card. Mirrors the Card wrapper and
 * inner padding of ProductInfoPanel, with lines for the rating, title/subtitle,
 * location, metadata tags, description and CTA.
 */
export const ProductHeroInfoCardSkeleton = () => (
  <Card className="md:max-w-[600px] shadow-sm">
    <div className="flex flex-col gap-4 p-6 md:px-10 md:py-8">
      {/* Star rating */}
      <div className={`h-5 w-56 max-w-full ${shimmer}`} />
      {/* Title + subtitle */}
      <div className="flex flex-col gap-2">
        <div className={`h-9 w-3/4 ${shimmer}`} />
        <div className={`h-5 w-1/2 ${shimmer}`} />
      </div>
      {/* Location line */}
      <div className={`h-6 w-40 ${shimmer}`} />
      {/* Metadata tags */}
      <div className="flex gap-2">
        <div className={`h-6 w-16 ${shimmer}`} />
        <div className={`h-6 w-20 ${shimmer}`} />
        <div className={`h-6 w-14 ${shimmer}`} />
      </div>
      {/* Description */}
      <div className="flex flex-col gap-2">
        <div className={`h-4 w-full ${shimmer}`} />
        <div className={`h-4 w-5/6 ${shimmer}`} />
        <div className={`h-4 w-4/6 ${shimmer}`} />
      </div>
      {/* CTA */}
      <div className="h-14 w-full rounded-full bg-gray-200 animate-pulse" />
    </div>
  </Card>
)

ProductHeroInfoCardSkeleton.displayName = "ProductHeroInfoCardSkeleton"

/**
 * Skeleton placeholder for the accordion list. Mirrors the collapsed
 * AccordionItem card (rounded-2xl, shadow, p-6) so no shift occurs when the
 * real accordions render.
 */
export const ProductHeroAccordionsSkeleton = ({
  count = 3
}: {
  count?: number
}) => (
  <div className="flex flex-col gap-4">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="rounded-2xl bg-white shadow-sm p-6">
        <div className={`h-7 w-1/2 ${shimmer}`} />
      </div>
    ))}
  </div>
)

ProductHeroAccordionsSkeleton.displayName = "ProductHeroAccordionsSkeleton"

export interface Props {
  // Gallery
  media: GalleryItem[]
  selectedMediaId?: string
  onMediaSelect?: (id: string) => void
  /**
   * When true, renders skeleton placeholders for the gallery, info card and
   * accordions instead of real content. Other content props are ignored.
   */
  loading?: boolean
  // ProductInfoPanel
  title: string
  ctaButton: ReactNode
  ctaLoading?: boolean
  /**
   * When true, a fixed bottom bar with the Get Tickets CTA shows on mobile
   * once the in-panel CTA scrolls out of view (handled by ProductInfoPanel).
   * Defaults to false (no fixed bar).
   */
  ctaFixedBar?: boolean
  urgencyIndicator?: ReactNode
  starRating?: { rating: number; text: string }
  subtitle?: string
  locationName?: string
  /**
   * Reserve vertical space for the location line while `locationName` is still
   * loading, to avoid layout shift when it arrives. See ProductInfoPanel.
   */
  reserveLocationSpace?: boolean
  metadataTags?: ReactNode
  description?: string
  features?: string[]
  availabilityNotice?: ReactNode
  finePrint?: string
  // Accordions
  accordionItems?: WithActivityTypeFormBulletFragment[]
  // Layout
  className?: string
}

const ProductHero = ({
  media,
  selectedMediaId,
  onMediaSelect,
  loading,
  title,
  ctaButton,
  ctaLoading,
  ctaFixedBar = false,
  urgencyIndicator,
  starRating,
  subtitle,
  locationName,
  reserveLocationSpace,
  metadataTags,
  description,
  features,
  availabilityNotice,
  finePrint,
  accordionItems,
  className
}: Props) => {
  const [internalSelectedId, setInternalSelectedId] = useState<string>(
    media[0]?.id ?? ""
  )

  const isControlled = selectedMediaId !== undefined
  const activeId = isControlled ? selectedMediaId : internalSelectedId
  const handleSelect = isControlled
    ? (onMediaSelect ?? (() => {}))
    : setInternalSelectedId

  if (loading) {
    return (
      <div className={classNames(className)}>
        <TwoColumnLayout
          left={<ProductHeroMediaSkeleton />}
          right={
            <div className="flex flex-col gap-6">
              <ProductHeroInfoCardSkeleton />
              <ProductHeroAccordionsSkeleton />
            </div>
          }
        />
      </div>
    )
  }

  return (
    <div className={classNames(className)}>
      <TwoColumnLayout
        left={
          <MediaGalleryWithThumbnails
            items={media}
            selectedId={activeId}
            onSelect={handleSelect}
          />
        }
        right={
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              {urgencyIndicator && (
                <Card
                  backgroundColor="#FEF2F2"
                  className="px-4 py-3 flex justify-center"
                >
                  {urgencyIndicator}
                </Card>
              )}
              <ProductInfoPanel
                title={title}
                ctaButton={ctaButton}
                ctaLoading={ctaLoading}
                ctaFixedBar={ctaFixedBar}
                starRating={starRating}
                subtitle={subtitle}
                locationName={locationName}
                reserveLocationSpace={reserveLocationSpace}
                metadataTags={metadataTags}
                description={description}
                features={features}
                availabilityNotice={availabilityNotice}
                finePrint={finePrint}
              />
            </div>
            {accordionItems && accordionItems.length > 0 && (
              <AccordionGroup>
                {accordionItems.map(item => (
                  <AccordionItem key={item.label} heading={item.label}>
                    <div
                      className="contentful-editor"
                      dangerouslySetInnerHTML={{ __html: item.value }}
                    />
                  </AccordionItem>
                ))}
              </AccordionGroup>
            )}
          </div>
        }
      />
    </div>
  )
}

ProductHero.displayName = "ProductHero"

export { ProductHero }
export default ProductHero
